/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import {
  Navigate,
  NavigateProps,
  Route,
  RouteProps,
  useLocation,
  matchRoutes,
} from "react-router-dom";
import { ERROR_TEXT } from "~/constants";
import { ACTION, useAccess } from "~/services/auth";
import { showError } from "~/services/toast";
import { ROUTES } from "./routes";

type CommonProps = {
  children: React.ReactElement;
  redirectTo?: string;
};

type Props = CommonProps & {
  action: ACTION;
};

export function RequireAccess({
  action,
  children,
  redirectTo = ROUTES.index,
}: Props) {
  const { hasAccess } = useAccess(action);

  return (
    <AccessHandler hasAccess={hasAccess} redirectTo={redirectTo}>
      {children}
    </AccessHandler>
  );
}

export function AccessHandler({
  hasAccess,
  children,
  redirectTo = ROUTES.accessDenied,
}: CommonProps & { hasAccess: boolean }) {
  if (hasAccess) {
    return children;
  }

  return (
    <RedirectWithMessage message={ERROR_TEXT.accessDenied} to={redirectTo} />
  );
}

function RedirectWithMessage({
  message,
  ...redirectProps
}: NavigateProps & { message: string }) {
  React.useEffect(() => {
    {
      showError(message);
    }
  }, [message]);

  return <Navigate replace {...redirectProps} />;
}

type MultiRoutes = RouteProps & {
  paths: string[];
};

export function renderMultiRoutes({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  element: Element,
  paths,
  ...rest
}: MultiRoutes) {
  return paths.map((path) => (
    <Route key={path} path={path} {...rest} element={Element} />
  ));
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const flattenObject = (object: Record<string, any>, prefix = "") =>
  Object.keys(object).reduce<Record<string, string>>((acc, k) => {
    const pre = prefix.length > 0 ? prefix + "." : "";

    if (typeof object[k] === "object") {
      Object.assign(acc, flattenObject(object[k], pre + k));
    } else {
      acc[pre + k] = object[k];
    }
    return acc;
  }, {});

const routes = Object.values(flattenObject(ROUTES)).map((value) => ({
  path: value,
}));

export function useCurrentPath() {
  const location = useLocation();
  const [{ route }] = matchRoutes(routes, location) ?? [{ route: undefined }];

  return route?.path;
}
